import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Modal,
    Button,
    IconButton,
    Fade,
    LinearProgress,
    useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { ClientData } from ".."; // Adjust import path
import { generateQuiz, QuizQuestion } from "../utils/quiz-generator";
import GlassContainer from "../../components/liquid-glass"; // Adjust path

interface ClientQuizModalProps {
    open: boolean;
    onClose: () => void;
    clients: ClientData[];
}

type GameState = "start" | "playing" | "result";

export default function ClientQuizModal({
    open,
    onClose,
    clients,
}: ClientQuizModalProps) {
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";

    const [gameState, setGameState] = useState<GameState>("start");
    const [questions, setQuestions] = useState<QuizQuestion[]>([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [score, setScore] = useState(0);
    const [selectedOption, setSelectedOption] = useState<string | null>(null);
    const [isAnswered, setIsAnswered] = useState(false);

    // Reset game when modal is opened
    useEffect(() => {
        if (open) {
            setGameState("start");
            setScore(0);
            setCurrentIndex(0);
            setIsAnswered(false);
            setSelectedOption(null);
        }
    }, [open]);

    const handleStart = () => {
        const newQuiz = generateQuiz(clients);
        if (newQuiz.length === 0) {
            // Handle insufficient clients case if needed, but UI should probably block it
            return;
        }
        setQuestions(newQuiz);
        setGameState("playing");
        setCurrentIndex(0);
        setScore(0);
        setIsAnswered(false);
        setSelectedOption(null);
    };

    const handleAnswer = (option: string) => {
        if (isAnswered) return;
        setSelectedOption(option);
        setIsAnswered(true);

        const currentQuestion = questions[currentIndex];
        if (option === currentQuestion.correctAnswer) {
            setScore((prev) => prev + 1);
        }

        // Auto-advance after delay
        setTimeout(() => {
            if (currentIndex < questions.length - 1) {
                setCurrentIndex((prev) => prev + 1);
                setIsAnswered(false);
                setSelectedOption(null);
            } else {
                setGameState("result");
            }
        }, 1500); // 1.5s delay to see result
    };

    const getOptionColor = (option: string) => {
        if (!isAnswered) {
            return isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)";
        }
        const currentQuestion = questions[currentIndex];
        if (option === currentQuestion.correctAnswer) {
            return "rgba(76, 175, 80, 0.3)"; // Green success
        }
        if (option === selectedOption) {
            return "rgba(244, 67, 54, 0.3)"; // Red error
        }
        return isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)";
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            closeAfterTransition
            sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backdropFilter: "blur(8px)",
            }}
        >
            <Fade in={open}>
                <Box sx={{ outline: "none", width: "100%", maxWidth: 500, p: 2 }}>
                    <GlassContainer
                        sx={{
                            p: 4,
                            borderRadius: 4,
                            position: "relative",
                            overflow: "hidden",
                        }}
                        isDark={isDark}
                    >
                        {/* Close Button */}


                        {/* --- START SCREEN --- */}
                        {gameState === "start" && (
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    textAlign: "center",
                                    gap: 3,
                                    py: 4,
                                }}
                            >
                                <EmojiEventsIcon
                                    sx={{ fontSize: 60, color: "#FFD700", mb: 1 }}
                                />
                                <Typography variant="h4" fontWeight="bold">
                                    Client Quiz
                                </Typography>
                                <Typography variant="body1" sx={{ opacity: 0.7 }}>
                                    Test your knowledge about your clients! Answer 5 questions based
                                    on your client data.
                                </Typography>

                                <Button
                                    variant="contained"
                                    size="large"
                                    onClick={handleStart}
                                    startIcon={<PlayArrowIcon />}
                                    sx={{
                                        mt: 2,
                                        borderRadius: 3,
                                        px: 5,
                                        py: 1.5,
                                        fontSize: "1.1rem",
                                        background: "linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)",
                                        boxShadow: "0 3px 15px rgba(33, 203, 243, .3)",
                                    }}
                                    disabled={clients.length < 3}
                                >
                                    Start Quiz
                                </Button>
                                {clients.length < 3 && (
                                    <Typography variant="caption" color="error">
                                        You need at least 3 clients to start the quiz.
                                    </Typography>
                                )}
                            </Box>
                        )}

                        {/* --- PLAYING SCREEN --- */}
                        {gameState === "playing" && questions.length > 0 && (
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                                {/* Progress */}
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                    <Typography variant="overline" sx={{ opacity: 0.7 }}>
                                        Question {currentIndex + 1} / {questions.length}
                                    </Typography>
                                    <Typography variant="overline" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                        Score: {score}
                                    </Typography>
                                </Box>

                                <LinearProgress
                                    variant="determinate"
                                    value={((currentIndex) / questions.length) * 100}
                                    sx={{
                                        height: 8,
                                        borderRadius: 4,
                                        bgcolor: isDark
                                            ? "rgba(255,255,255,0.1)"
                                            : "rgba(0,0,0,0.1)",
                                        "& .MuiLinearProgress-bar": {
                                            borderRadius: 4,
                                            background:
                                                "linear-gradient(90deg, #2196F3 0%, #21CBF3 100%)",
                                        },
                                    }}
                                />

                                {/* Question */}
                                <Typography variant="h5" fontWeight="600" sx={{ minHeight: 60 }}>
                                    {questions[currentIndex].question}
                                </Typography>

                                {/* Options */}
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                                    {questions[currentIndex].options.map((option, idx) => {
                                        const isCorrect = option === questions[currentIndex].correctAnswer;
                                        const isSelected = option === selectedOption;

                                        let borderColor = "transparent";
                                        if (isAnswered) {
                                            if (isCorrect) borderColor = "#4CAF50";
                                            else if (isSelected) borderColor = "#F44336";
                                        }

                                        return (
                                            <Button
                                                key={idx}
                                                onClick={() => handleAnswer(option)}
                                                disabled={isAnswered}
                                                sx={{
                                                    justifyContent: "space-between",
                                                    px: 3,
                                                    py: 2,
                                                    borderRadius: 3,
                                                    bgcolor: getOptionColor(option),
                                                    border: `2px solid ${borderColor}`,
                                                    color: isDark ? "white" : "black",
                                                    "&:hover": {
                                                        bgcolor: isAnswered ? getOptionColor(option) : (isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"),
                                                    },
                                                    textTransform: "none",
                                                    fontSize: "1rem",
                                                    transition: "all 0.2s ease",
                                                }}
                                            >
                                                {option}
                                                {isAnswered && isCorrect && <CheckCircleIcon sx={{ color: "#4CAF50" }} />}
                                                {isAnswered && isSelected && !isCorrect && <CancelIcon sx={{ color: "#F44336" }} />}
                                            </Button>
                                        )
                                    })}
                                </Box>
                            </Box>
                        )}

                        {/* --- RESULT SCREEN --- */}
                        {gameState === "result" && (
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    textAlign: "center",
                                    gap: 3,
                                    py: 4,
                                }}
                            >
                                <EmojiEventsIcon
                                    sx={{ fontSize: 80, color: score === 5 ? "#FFD700" : score >= 3 ? "#C0C0C0" : "#CD7F32", mb: 1 }}
                                />
                                <Typography variant="h3" fontWeight="bold">
                                    {score} / {questions.length}
                                </Typography>
                                <Typography variant="h6">
                                    {score === 5
                                        ? "Perfect Score! You know your clients well!"
                                        : score >= 3
                                            ? "Great job! Keep it up!"
                                            : "Good effort! Try again to improve."}
                                </Typography>

                                <Button
                                    variant="contained"
                                    size="large"
                                    onClick={handleStart} // Replay
                                    startIcon={<PlayArrowIcon />}
                                    sx={{
                                        mt: 2,
                                        borderRadius: 3,
                                        px: 5,
                                        py: 1.5,
                                        fontSize: "1.1rem",
                                        background: "linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)",
                                    }}
                                >
                                    Play Again
                                </Button>
                            </Box>
                        )}
                    </GlassContainer>
                </Box>
            </Fade>
        </Modal>
    );
}
